import React, { Dispatch, SetStateAction, useState } from 'react';
import { Dialog, DialogContent, DialogActions, Button } from '@mui/material';
import JSONEditor from '@src/components/_elements/JSONEditor/JSONEditor';
import { GenerateDevicePayload } from '@src/types/Collection';
import { useQueryClient } from '@tanstack/react-query';
import QueryKeys from '@src/constants/queryKeys';
import useDeviceMutations from '@src/hooks/api/mutations/useDeviceMutations';
import storeConnector from '@src/store/storeConnector';
import { SiteMeta } from '@src/types/SiteMeta';
import deviceSx from '../configs/DeviceSx';

interface DialogProps {
  siteMeta: SiteMeta;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setConfirmOpen: Dispatch<SetStateAction<boolean>>;
}

const genPayload = {
  unitControllerIds: ['0000001F', '0000002F'],
  sourceDevices: [
    {
      type: 'tsc',
      parentType: 'siteController',
      count: 4,
    },
    {
      type: 'mp',
      parentType: 'tsc',
      count: 4,
      namePrefix: 'Megapack',
    },
    {
      type: 'bms',
      parentType: 'unitController',
      count: 4,
    },
    {
      type: 'rack',
      parentType: 'bms',
      count: 2,
    },
    {
      type: 'meter',
      parentType: 'siteController',
      count: 2,
    },
    {
      type: 'pcs',
      parentType: 'unitController',
      count: 2,
    },
  ],
};

function GenerateDeviceDialog({
  siteMeta,
  setOpen,
  setConfirmOpen,
}: DialogProps) {
  const [newPayload, setNewPayload] = useState<GenerateDevicePayload>({
    ...genPayload,
    siteControllerId: siteMeta.siteId,
  });
  const queryClient = useQueryClient();
  const { generateDevices } = useDeviceMutations();

  const handleGenDeviceSave = () =>
    generateDevices.mutate(newPayload, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.devices],
        });
        setConfirmOpen(true);
        setOpen(false);
      },
    });

  return (
    <Dialog
      open
      PaperProps={{
        sx: deviceSx.genDialogPaper,
      }}
    >
      <DialogContent>
        <JSONEditor
          json={newPayload}
          onChange={setNewPayload}
          options={{ mode: 'code' }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
        <Button size='small' variant='contained' onClick={handleGenDeviceSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default storeConnector(GenerateDeviceDialog, {
  config: ['siteMeta'],
  user: ['role'],
});
